import React, { useEffect, useState } from 'react';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import client from '../../utils/fetchClient';
import { useProjectContext } from '../../context/ProjectContext';

export default function FailureAnalysisPage() {
  const { effectiveProjectParam } = useProjectContext();
  const [state, setState] = useState({ loading: true, error: null, failures: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const q = effectiveProjectParam ? { project: effectiveProjectParam } : {};
        const testRc = await client.get('/api/root-causes', { query: q });
        const failures = testRc.ok ? (await testRc.json()).data || [] : [];
        if (!cancelled) setState({ loading: false, error: null, failures });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || 'Failed to load', failures: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveProjectParam]);

  const { loading, error, failures } = state;

  return (
    <Page maxW="6xl">
      <Breadcrumbs items={[{ label: 'AI Analysis', to: '/analysis/release-readiness' }, { label: 'Failure Analysis' }]} />
      <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900" mb={1}>Failure Analysis</Heading>
      <Text fontSize="sm" color="gray.500" mb={5}>Where test failures cluster — by recurring error message and affected suite.</Text>

      {loading && <LoadingState message="Analysing failures" />}
      {error && <ErrorState message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        <VStack align="stretch" spacing={5}>
          <SectionCard>
            <Heading size="sm" color="gray.900" mb={3}>Recurring test errors</Heading>
            <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
              {failures.slice(0, 12).map((f, i) => (
                <Box key={f.category || i} py={2.5}>
                  <Text fontSize="13px" color="status.fail" fontFamily="mono" noOfLines={1}>{f.category}</Text>
                  <Text fontSize="11px" color="gray.400">
                    {f.count} occurrence{f.count > 1 ? 's' : ''}
                    {f.examples?.[0]?.suite ? ` · ${f.examples[0].suite}` : ''}
                  </Text>
                </Box>
              ))}
              {failures.length === 0 && <Text fontSize="sm" color="gray.400" py={2}>No failing tests in range.</Text>}
            </VStack>
          </SectionCard>
        </VStack>
      )}
    </Page>
  );
}
